import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import RepeatIcon from '@mui/icons-material/Repeat';
import type { StrategyListItemV2 } from '../../types/strategy';
import { traderTheme as theme } from './traderDashboardTheme';
import { getExchangeLogoByName } from '../../utils/exchangeUrls';

export interface UpdateReplaySettingsDialogProps {
  open: boolean;
  strategy: StrategyListItemV2 | null;
  onClose: () => void;
  onConfirm: (replayEnabled: boolean, replayDelaySeconds: number) => void;
  loading: boolean;
}

export const UpdateReplaySettingsDialog: React.FC<UpdateReplaySettingsDialogProps> = ({
  open,
  strategy,
  onClose,
  onConfirm,
  loading,
}) => {
  const { t } = useTranslation();
  const [replayEnabled, setReplayEnabled] = useState(false);
  const [delaySeconds, setDelaySeconds] = useState('0');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !strategy) return;
    setReplayEnabled(strategy.replay_enabled);
    setDelaySeconds(String(strategy.replay_delay_seconds ?? 0));
    setError('');
  }, [open, strategy]);

  const handleConfirm = () => {
    const parsedDelay = Number(delaySeconds);
    if (!Number.isFinite(parsedDelay) || parsedDelay < 0 || !Number.isInteger(parsedDelay)) {
      setError(t('traderDashboard.updateReplaySettings.errorInvalidDelay'));
      return;
    }
    setError('');
    onConfirm(replayEnabled, parsedDelay);
  };

  const symbol = strategy?.symbol ?? '-';
  const exchangeName = strategy?.spot_exchange_name ?? '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.bg.primary,
          border: `1px solid ${theme.border}`,
          color: theme.text.primary,
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.bg.primary,
          borderBottom: `1px solid ${theme.border}`,
          color: theme.accent,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {strategy && (
            <Avatar
              src={getExchangeLogoByName(exchangeName)}
              sx={{ width: 28, height: 28 }}
            />
          )}
          <RepeatIcon sx={{ color: theme.accent }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: theme.accent }}>
            {t('traderDashboard.updateReplaySettings.title', { symbol })}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" sx={{ color: theme.text.secondary, mb: 2.5 }}>
          {t('traderDashboard.updateReplaySettings.description')}
        </Typography>

        {strategy && (
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.bg.hover,
              borderRadius: 1,
              mb: 3,
              border: `1px solid ${theme.border}`,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: theme.text.secondary, mb: 1 }}>
              {t('traderDashboard.updateReplaySettings.currentConfig')}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.text.secondary }}>
              {t('traderDashboard.updateReplaySettings.currentReplay')}:{' '}
              <Typography component="span" sx={{ color: strategy.replay_enabled ? theme.profit : theme.loss, fontWeight: 600 }}>
                {strategy.replay_enabled
                  ? t('traderDashboard.updateReplaySettings.enabled')
                  : t('traderDashboard.updateReplaySettings.disabled')}
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: theme.text.secondary, mt: 0.5 }}>
              {t('traderDashboard.updateReplaySettings.currentDelay')}:{' '}
              <Typography component="span" sx={{ color: theme.accent, fontWeight: 600 }}>
                {strategy.replay_delay_seconds}s
              </Typography>
            </Typography>
            {strategy.is_replay && (
              <Typography variant="body2" sx={{ color: theme.text.secondary, mt: 0.5 }}>
                {t('traderDashboard.updateReplaySettings.currentOrigin')}:{' '}
                <Typography component="span" sx={{ color: theme.text.primary, fontFamily: 'monospace' }}>
                  {strategy.replay_source_strategy_id ?? '-'}
                </Typography>
              </Typography>
            )}
          </Box>
        )}

        {strategy?.is_replay && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('traderDashboard.updateReplaySettings.replayOriginNotice')}
          </Alert>
        )}

        <FormControlLabel
          control={(
            <Switch
              checked={replayEnabled}
              onChange={(event) => setReplayEnabled(event.target.checked)}
            />
          )}
          label={t('traderDashboard.updateReplaySettings.enableReplay')}
          sx={{ mb: 2, color: theme.text.primary }}
        />

        <TextField
          label={t('traderDashboard.updateReplaySettings.delaySeconds')}
          type="number"
          value={delaySeconds}
          onChange={(event) => setDelaySeconds(event.target.value)}
          fullWidth
          inputProps={{ min: 0, step: 1 }}
          helperText={t('traderDashboard.updateReplaySettings.delayHelp')}
          sx={{
            '& .MuiInputBase-root': {
              color: theme.text.primary,
              backgroundColor: theme.bg.secondary,
            },
            '& .MuiFormLabel-root': {
              color: theme.text.secondary,
            },
            '& .MuiFormHelperText-root': {
              color: theme.text.secondary,
            },
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={loading} sx={{ color: theme.text.secondary }}>
          {t('traderDashboard.clearPosition.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || !strategy}
          startIcon={loading ? <CircularProgress size={16} /> : <RepeatIcon />}
          sx={{
            backgroundColor: theme.accent,
            color: theme.bg.primary,
            '&:hover': {
              backgroundColor: theme.accentHover,
            },
          }}
        >
          {loading
            ? t('traderDashboard.updateReplaySettings.updating')
            : t('traderDashboard.updateReplaySettings.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateReplaySettingsDialog;
